import { createRoute, useNavigate, useSearch } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { appRoute } from "./route";
import { PageContainer, PageHeader } from "@fleet/ui";
import { PurchasingManager } from "@/features/purchasing/components/purchasing-manager";
import { RestaurantGate } from "@/features/pos/components/restaurant-gate";
import { useActiveContext } from "@/features/pos/hooks/use-active-context";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Purchasing search params — make state SHAREABLE by URL (campaign #15b,
 * same pattern as POS `?order=`):
 *   - `po`: selected purchase order (open lines panel). Copy/paste
 *     URL reopens that detail on another device/tab; if id no longer exists
 *     lines list simply remains empty (no hanging loader).
 */
interface PurchasingSearch {
  po?: string;
}

function PurchasingPage() {
  const { t } = useI18n();
  const ctx = useActiveContext();
  const search = useSearch({ from: purchasingRoute.id });
  const navigate = useNavigate();
  return (
    <PageContainer maxWidth="5xl">
      <PageHeader title={t(msg`Purchasing`)} />
      <RestaurantGate ctx={ctx}>
        {(c) => (
          <PurchasingManager
            org={c.org}
            branch={c.branch}
            poParam={() => search().po}
            onPoChange={(po) => {
              void navigate({
                to: "/purchasing",
                search: (prev: PurchasingSearch) => ({ ...prev, po: po ?? undefined }),
                replace: true,
              });
            }}
          />
        )}
      </RestaurantGate>
    </PageContainer>
  );
}

export const purchasingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/purchasing",
  validateSearch: (search: Record<string, unknown>): PurchasingSearch => ({
    po: typeof search.po === "string" && search.po.trim() !== "" ? search.po : undefined,
  }),
  component: PurchasingPage,
});
